/**
 * `SystemSchemeDetector` backed by `prefers-color-scheme` — the host hook for
 * every shell served in a plain browser context: web-ui, mobile (PWA) and
 * desktop (the Tauri webview reports the OS scheme through the same media
 * query). mini-app is the exception and keeps its own Telegram-backed detector,
 * because inside a Mini App the surrounding app's theme wins over the OS.
 *
 * IT LIVES IN app-core, NOT IN THE SHELLS, and that is the whole point. This is
 * the same object for three surfaces; three copies would be three chances to
 * diverge on a detail like the `change`-vs-`addListener` fallback below. The
 * shell decides WHICH detector to use — that is the ADR-079 seam — it does not
 * re-implement the common one.
 *
 * THE TWO FALLBACKS BELOW ARE CURRENTLY UNREACHABLE FROM ANY LIVE CALLER, and
 * that is stated here rather than argued about (umnyi, reviewing web!227):
 *
 *   - the `matchMedia`-absent branch: `matchMedia` is universal in browsers
 *     since ~2015 and none of the four shells server-render, so no production
 *     path reaches it. The only caller that does is this package's own test,
 *     which STUBS matchMedia in — i.e. the branch exists for an environment we
 *     do not ship to.
 *   - the legacy `addListener` branch: Safari gained
 *     `MediaQueryList.addEventListener` in 14 (2020); desktop and mobile would
 *     need a WebKit older than that to take it.
 *
 * They stay because the failure they prevent is silent and the cost is six
 * lines — but "should this degrade or throw?" cannot be settled by argument
 * while BOTH answers describe a path production never takes. If you are here to
 * change it, first establish that something reaches it; if nothing does, the
 * change is a style preference and should be argued as one.
 *
 * The degrade choice itself: a missing media query means "cannot tell", and the
 * honest answer to that is the documented default (readThemePref also defaults
 * to 'system' → 'light'), not a crash on a surface where the user never asked
 * about theming.
 */
const QUERY = '(prefers-color-scheme: dark)';
export const browserSystemSchemeDetector = {
    getScheme: () => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return 'light';
        return window.matchMedia(QUERY).matches ? 'dark' : 'light';
    },
    subscribe: (onChange) => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function')
            return () => { };
        const mql = window.matchMedia(QUERY);
        // Safari < 14 (and the WKWebView of that era, which the desktop/mobile
        // shells can still meet) has no addEventListener on MediaQueryList — only
        // the deprecated addListener. Feature-detect rather than branch on a UA
        // string, and unsubscribe through whichever half we actually attached to.
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange);
            return () => mql.removeEventListener('change', onChange);
        }
        const legacy = mql;
        legacy.addListener?.(onChange);
        return () => legacy.removeListener?.(onChange);
    },
};
//# sourceMappingURL=browserSchemeDetector.js.map